import { useEffect, useState } from "react";
import { dismissModal } from "@/utils/modalClose";
import { createPortal } from "react-dom";
import { IonHeader, IonToolbar, IonTitle, IonButtons, IonButton, IonIcon, IonSkeletonText, IonSpinner } from "@ionic/react";
import { closeOutline, documentTextOutline } from "ionicons/icons";
import JSZip from "jszip";
import PreviewPager from "./PreviewPager";
import { saveBlob } from "@/utils/archivedFile";
import { pdfToPageImages } from "../utils/pdfPageImages";

// Renders a stored file (PDF, or a ZIP of PDFs) to page images — the same
// swipeable pager the /app notification preview uses.
export async function fileToPreviewPages(blob, fileName = "") {
  const isZip = /\.zip$/i.test(fileName) || blob.type === "application/zip";
  if (!isZip) return pdfToPageImages(await blob.arrayBuffer());
  const zip = await JSZip.loadAsync(blob);
  const entries = Object.values(zip.files).filter((f) => !f.dir && /\.pdf$/i.test(f.name)).sort((a, b) => a.name.localeCompare(b.name));
  const pages = [];
  for (const entry of entries.slice(0, 12)) {
    pages.push(...(await pdfToPageImages(await entry.async("arraybuffer"), { maxPages: 4 })));
    if (pages.length >= 24) break;
  }
  return pages;
}

// The /app notification preview modal, for any stored document: a portalled
// overlay (centered 600px card on desktop, full-screen on mobile) with the
// file name in the toolbar, a full-size skeleton while pages render, the
// PreviewPager, and a Download button. `loadFile` returns the Blob.
export default function DocumentPreviewModal({ doc, loadFile, onClose: onCloseNow, zIndex = 30000 }) {
  const onClose = (e) => dismissModal(onCloseNow, e); // animated exit, then the real close

  const [pages, setPages] = useState([]);
  const [index, setIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [blob, setBlob] = useState(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    let alive = true;
    setPages([]); setIndex(0); setFailed(false); setBlob(null);
    if (!doc) return undefined;
    setLoading(true);
    (async () => {
      try {
        await new Promise((r) => setTimeout(r, 50)); // let the skeleton paint
        const file = await loadFile(doc);
        if (!alive) return;
        setBlob(file);
        const imgs = await fileToPreviewPages(file, doc.fileName);
        if (alive) setPages(imgs);
      } catch (err) {
        console.error("Document preview failed:", err);
        if (alive) setFailed(true);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [doc]); // eslint-disable-line

  if (!doc) return null;
  const desktop = window.innerWidth >= 768;

  const download = async () => {
    setDownloading(true);
    try {
      const file = blob || (await loadFile(doc));
      saveBlob(file, doc.fileName || "document");
    } catch (err) {
      console.error("Download failed:", err);
    } finally {
      setDownloading(false);
    }
  };

  return createPortal(
    <div className="modal-backdrop" onClick={onClose} style={{ position: "fixed", inset: 0, zIndex, background: desktop ? "rgba(0,0,0,0.5)" : "var(--ion-background-color, #f2f2f7)", display: "flex", alignItems: desktop ? "center" : "stretch", justifyContent: desktop ? "center" : "stretch" }}>
      <div className="modal-slide-up" onClick={(e) => e.stopPropagation()} style={{ background: "var(--ion-background-color, #f2f2f7)", color: "var(--ion-text-color)", display: "flex", flexDirection: "column", width: "100%", maxWidth: desktop ? 600 : "100%", height: desktop ? "auto" : "100%", maxHeight: desktop ? "90vh" : "100%", overflow: "hidden" }}>
        <IonHeader>
          <IonToolbar style={{ "--background": "var(--ion-card-background)", "--color": "var(--ion-text-color)" }}>
            <IonButtons slot="start">
              <IonButton fill="clear" shape="round" onClick={onClose}>
                <span slot="icon-only" style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", lineHeight: 0, flexShrink: 0, fontSize: "1.8em", color: "var(--ion-text-color)" }}>
                  <IonIcon icon={closeOutline} style={{ fontSize: "inherit", color: "inherit", pointerEvents: "none" }} />
                </span>
              </IonButton>
            </IonButtons>
            <IonTitle style={{ fontWeight: 700 }}>{doc.fileName || "Preview"}</IonTitle>
          </IonToolbar>
        </IonHeader>
        <div style={{ flex: 1, overflowY: "auto", padding: 16 }}>
          {loading ? (
            <div style={{ position: "relative", paddingTop: "141.4%", overflow: "hidden", borderRadius: 8 }}>
              <IonSkeletonText animated={true} style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", margin: 0, borderRadius: 0 }} />
            </div>
          ) : pages.length > 0 ? (
            <PreviewPager pages={pages} index={index} onIndexChange={setIndex} altPrefix={doc.fileName || "Preview"} />
          ) : (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: 320, background: "var(--ion-color-step-100)", borderRadius: 8, border: "2px dashed var(--ion-color-light-shade)" }}>
              <IonIcon icon={documentTextOutline} style={{ fontSize: "2.5rem", color: failed ? "var(--ion-color-danger)" : "var(--ion-color-medium)", marginBottom: 8 }} />
              <p style={{ fontSize: "0.8rem", color: "var(--ion-color-medium)", textAlign: "center", margin: 0, lineHeight: 1.5 }}>
                {failed ? "Couldn't load this file." : "Preview isn't available for this file, but you can still download it."}
              </p>
            </div>
          )}

          <div style={{ marginTop: 20, display: "flex", flexDirection: "column", gap: 10 }}>
            <IonButton expand="block" color="success" disabled={downloading || loading} onClick={download}>
              {downloading && <IonSpinner name="crescent" slot="start" style={{ width: 14, height: 14, color: "var(--ion-color-success-contrast)" }} />}
              Download
            </IonButton>
          </div>
        </div>
      </div>
    </div>,
    document.querySelector("ion-app") || document.body
  );
}
